import type {ProgramSignalThresholdViolationInterface} from '@shared/interfaces/programSignalThreshold'
import type {PublishedPageContext} from '@shared/alpinist/pageContext'

/**
 * The first message of an "Investigate" conversation opened from an alert link
 * (AB#6581): the alert as the platform recorded it, plus the ids Alpinist needs
 * to go and look. Built here from the violation id the link carried -- never
 * from text in the link -- and in English on purpose: it is addressed to the
 * agent, which answers in the user's language. Same shape as the benchmark
 * Investigate prompt (Benchmark/investigatePrompt.ts): the case first, then
 * what to do.
 */

const programIds = (
  program: ProgramSignalThresholdViolationInterface['program_signal_threshold']['program'],
): string[] => (program ?? []).map((entry) => (typeof entry === 'string' ? entry : entry.id))

const howItIsJudged = (
  threshold: ProgramSignalThresholdViolationInterface['program_signal_threshold'],
): string => {
  if (threshold.evaluation_mode === 'duration') {
    const limits = [
      threshold.max_duration ? `longer than ${threshold.max_duration}` : null,
      threshold.min_duration ? `shorter than ${threshold.min_duration}` : null,
    ].filter(Boolean)
    return `a duration alert: it fires when one unbroken stretch of the condition lasts ${limits.join(' or ')} (HH:MM:SS)`
  }
  return `a value alert: it fires while the condition holds for at least ${threshold.min_hold_time || '00:00:00'} (HH:MM:SS)`
}

export const buildInvestigateViolationPrompt = (
  violation: ProgramSignalThresholdViolationInterface,
): string => {
  const threshold = violation.program_signal_threshold
  const programs = programIds(threshold.program)
  const triggers = (threshold.triggers ?? []).map(
    (trigger) =>
      `"${trigger.signal?.name}" (signal id ${trigger.signal?.id}) ${trigger.rule} ${trigger.threshold_display ?? trigger.active_threshold}`,
  )

  return [
    `I want to investigate the alert "${threshold.name}" (threshold violation id ${violation.id}).`,
    '',
    'THE CASE (as the platform recorded it -- do not recompute it):',
    `- alert: ${threshold.name}${threshold.description ? ` -- ${threshold.description}` : ''}`,
    `- how it is judged: ${howItIsJudged(threshold)}`,
    ...(violation.condition_display ? [`- condition: ${violation.condition_display}`] : []),
    violation.t_end
      ? `- when: from ${violation.t_start} to ${violation.t_end} (UTC)${violation.violation_duration ? `, lasting ${violation.violation_duration}` : ''}`
      : `- when: from ${violation.t_start} (UTC), still going when the alert was sent`,
    `- equipment: ${violation.equipment_name ?? 'unknown'}${violation.machine ? `, line (machine) id ${violation.machine}` : ''}`,
    `- threshold id ${threshold.id}${programs.length ? `, program id${programs.length > 1 ? 's' : ''} ${programs.join(', ')}` : ''}`,
    ...(triggers.length
      ? [
          `- conditions (${threshold.triggers_truth_eval === 'any' ? 'any of' : 'all of'}):`,
          ...triggers.map((line) => `  - ${line}`),
        ]
      : []),
    '',
    'Look at these signals and anything related on this equipment around this window, and compare with similar periods when the alert did not fire.',
    'Start by telling me in a few lines what happened and what you would check first. Then look, and say what you find, the most likely cause, and what I could ask you next.',
  ].join('\n')
}

/** The alert's references as the shortcut's page_context (AB#6762). */
export const investigateViolationPageContext = (
  violation: ProgramSignalThresholdViolationInterface,
): PublishedPageContext => {
  const threshold = violation.program_signal_threshold
  const signalIds = (threshold.triggers ?? [])
    .map((trigger) => trigger.signal?.id)
    .filter((id): id is string => Boolean(id))
  return {
    entities: {
      line_id: violation.machine,
      program_id: programIds(threshold.program)[0],
      signal_ids: signalIds,
    },
    time_range: {start: violation.t_start, end: violation.t_end, preset: null},
  }
}
